<template>
  <ProductList
    v-if="!showIsLoading && !isLoading"
    :items="displayedProducts"
    :size="size"
  >
    <template #card-footer="productCardProps">
      <ProductCardFooter
        :has-fast-delivery="productIdsByDelivery[productCardProps.product.id]"
        :product-card-props="productCardProps"
        :secondary-link="getSecondaryLink(productCardProps.product)"
        :secondary-link-text="
          $textDictionary['productSearch.linkToSeries.label'] ??
          'See product series'
        "
      />
    </template>
  </ProductList>
  <ProductSearchSkeleton v-else :item-count="stackSize" />
</template>

<script setup lang="ts">
import { ProductList } from '@hypercodestudio/basler-components';

import ProductCardFooter from './ProductCardFooter.vue';
import type {
  CommonProductDetailFragment,
  CommonProductFragment
} from '~/lib/Shop/generated/schema';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { isDefined } from '~/utils/guards/isDefined';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { RelatedProductGroupModel } from '~/utils/shop/DecoratedProduct';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import ProductSearchSkeleton from '~/components/shop/skeletons/ProductSearchSkeleton.vue';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { processProductVariants } from '~/utils/shop/processProductVariants';

type Sizes = 'threequaters' | 'three-quarters' | 'full';

type Props = {
  group: RelatedProductGroupModel;
  size: Sizes;
  generateDetailLink: (p: DecoratedProduct) => string;
  generateSeriesLink: (p: DecoratedProduct) => HyperlinkInterface | undefined;
  generateProductPosition: (p: DecoratedProduct) => string;
  generateProductObjectId: (p: DecoratedProduct) => string;
  stackSize?: number;
  showIsLoading?: boolean;
};

const props = defineProps<Props>();
const { $textDictionary, $locale, $shopStore, $globalPageSettings } =
  useNuxtApp();

const isLoading = ref(false);
const isSkeletonTimeOutPassed = ref(false);
const skeletonTimeout = ref<NodeJS.Timeout>();
const error = ref<Error | null>(null);
const abortController = ref<AbortController | undefined>(undefined);
// Make sure stackSize is at least the number of SKUs we have
const stackSize = computed(() => {
  const size = props.stackSize ?? 9;
  return Math.max(size, skus.value.length);
});
const buttonPressed = ref(1);
const products = ref<(CommonProductFragment | CommonProductDetailFragment)[]>(
  []
);
const lastResponseProducts = ref<
  (CommonProductFragment | CommonProductDetailFragment)[]
>([]);
const lastResponseTotalCount = ref<number | null>(null);

const skus = ref<string[]>(
  props.group.products.map((p) => p.sku).filter(isDefined)
);

onMounted(() => {
  skeletonTimeout.value = setTimeout(
    () => (isSkeletonTimeOutPassed.value = true),
    250
  );
});

const getSecondaryLink = (product: any) => {
  return props.generateSeriesLink(product);
};

function filterSkus(skus: any[]): string[] {
  return skus
    .map((item) => {
      // If item is an array, find the first valid SKU string
      if (Array.isArray(item)) {
        const validItem = item.find(
          (subItem) =>
            typeof subItem === 'string' && /^[a-zA-Z0-9][\w-]*$/.test(subItem)
        );
        return validItem || '';
      }
      // If item is a string and is a valid SKU format, return it
      if (typeof item === 'string' && /^[a-zA-Z0-9][\w-]*$/.test(item)) {
        return item;
      }
      return '';
    })
    .filter(Boolean);
}

const doRequest = async () => {
  if (!skus.value.length) {
    return;
  }

  if (!abortController.value?.signal.aborted) {
    abortController.value?.abort();
  }

  const currentAbortController = new AbortController();
  abortController.value = currentAbortController;

  isLoading.value = true;
  error.value = null;

  const filteredSkus = filterSkus(skus.value);

  try {
    const response = await $fetch<{
      items: (CommonProductFragment | CommonProductDetailFragment)[];
      total_count: number;
    }>('/api/magento/products', {
      ...DEFAULT_FETCH_OPTIONS,
      query: {
        store: $shopStore.value,
        locale: $locale.value,
        skus: filteredSkus,
        pageSize: stackSize.value,
        filters: props.group.queryFilter || {}
      },
      signal: currentAbortController.signal
    });

    // Check if we have variants in some results. if so, try to get the original product by variants sku
    lastResponseProducts.value = processProductVariants(
      response?.items || [],
      skus.value
    );
    lastResponseTotalCount.value = response?.total_count;

    products.value = [...products.value, ...lastResponseProducts.value];

    // try to sort products to keep the original order that was given by skus
    products.value = products.value.sort((a, b) => {
      const indexA = skus.value.indexOf(a.sku ?? '');
      const indexB = skus.value.indexOf(b.sku ?? '');
      return indexA - indexB;
    });

    error.value = null;
  } catch (e: unknown) {
    // ignore abort exceptions
    if (currentAbortController.signal.aborted) {
      return;
    }
    error.value = e as Error;
  } finally {
    isLoading.value = false;
  }
};

const createProductDetailLink = useCreateProductDetailLink();

const pdpConfiguration = usePdpConfiguration();
const displayedProducts = computed(() => {
  const configuration = pdpConfiguration.value;
  if (!configuration) {
    // console.log('ProductSearchList: No pdpConfiguration available');
    return [];
  }

  return products.value.map((x) => {
    const decoratedProduct = DecoratedProduct.build(
      x,
      $textDictionary.value,
      $locale.value
    ).asListEntry(
      configuration,
      {},
      createProductDetailLink,
      undefined,
      undefined,
      $globalPageSettings.value?.defaultProductImage ?? undefined
    );

    const position =
      props.generateProductPosition(decoratedProduct.product as any) ?? 1;
    const objectId =
      props.generateProductObjectId(decoratedProduct.product as any) ?? '';
    return {
      ...decoratedProduct,
      cardDataAttributes: {
        'insights-object-id': objectId,
        'insights-position': position
      }
    };
  });
});

type ProductByDelivery = Record<string, boolean>;

const productIdsByDelivery = computed<ProductByDelivery>(() => {
  return products.value.reduce((prev, product) => {
    const decoratedProduct = DecoratedProduct.build(product);
    return { ...prev, [decoratedProduct.id]: decoratedProduct.hasFastDelivery };
  }, {});
});

watch(
  () => props.group.products,
  () => {
    const newSkus = props.group.products.map((p) => p.sku).filter(isDefined);
    if (
      skus.value.length !== newSkus.length ||
      !skus.value.every((sku) => newSkus.includes(sku))
    ) {
      skus.value = newSkus;
    }
  }
);

watch(skus, () => {
  // reset products once groups change
  products.value = [];
  buttonPressed.value = 0;
});

watch(
  skus,
  () => {
    doRequest();
  },
  { immediate: true }
);
</script>
